/* eslint-disable max-lines */
import type Konva from "konva";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { notifyError } from "common/utils/app-monitoring/notify";
import { Group, Image, Rect } from "react-konva";

import { type InteractionMode, useRefineHistoryContext } from "@design-lab/entities/artwork";
import { trackImageEditCrop } from "@design-lab/shared/lib/analytics";
import type { PlacementFormPosition } from "@design-lab/shared/types";

import type { ArtworkDimensions } from "./ArtworkGroup";
import { CropTransformer } from "./CropTransformer";
import { EraseOverlay } from "./EraseOverlay";

export type RefineArtworkGroupProps = {
  artworkId: string;
  placement: PlacementFormPosition;
  artworkImage?: HTMLImageElement | undefined;
  artworkDimensions?: ArtworkDimensions | undefined;
  printableArea?: { left: number; top: number; width: number; height: number } | undefined;
  activeTool?: string;
  interactionMode?: InteractionMode;
  isSpaceHeld?: boolean;
};

export function RefineArtworkGroup({
  artworkId,
  artworkImage,
  artworkDimensions,
  printableArea,
  activeTool,
  interactionMode = "cursor",
  isSpaceHeld = false
}: RefineArtworkGroupProps) {
  const { currentImageData, currentOperation, pushOperation, seedBaseline } =
    useRefineHistoryContext();
  const imageRef = useRef<Konva.Image>(null);
  const groupRef = useRef<Konva.Group>(null);
  const originalDimensionsRef = useRef<
    { x: number; y: number; width: number; height: number } | undefined
  >(undefined);
  const isCroppingRef = useRef<boolean>(false);

  // Capture baseline once when first available and store baseline image + dimensions
  useEffect(() => {
    if (!originalDimensionsRef.current && artworkDimensions && artworkImage) {
      if (!artworkImage.complete || artworkImage.naturalWidth === 0) return;
      originalDimensionsRef.current = {
        x: artworkDimensions.x,
        y: artworkDimensions.y,
        width: artworkDimensions.width,
        height: artworkDimensions.height
      };
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (ctx) {
          canvas.width = artworkImage.width;
          canvas.height = artworkImage.height;
          ctx.drawImage(artworkImage, 0, 0);
          const dataUrl = canvas.toDataURL();
          seedBaseline(dataUrl, originalDimensionsRef.current);
        }
      } catch (e) {
        // no-op
        notifyError("RefineArtworkGroup seedBaseline error", {
          params: {
            error: e instanceof Error ? e.message : String(e),
            error_object: e instanceof Error ? e.toString() : String(e),
            stack_trace: e instanceof Error ? e.stack : null,
            component: "RefineArtworkGroup",
            action: "seedBaseline"
          }
        });
      }
    }
  }, [artworkDimensions, artworkImage, seedBaseline]);

  const displayDimensions = useMemo(() => {
    const historyDims = currentOperation?.metadata?.dimensions;
    if (!historyDims || !printableArea) {
      return historyDims;
    }

    // Calculate the aspect ratio of the edited image
    const aspectRatio = historyDims.height / historyDims.width;

    // Re-fit to the CURRENT printable area
    const maxWidth = printableArea.width;
    const maxHeight = printableArea.height;
    const maxAllowedWidth = Math.min(maxWidth, maxHeight / aspectRatio);

    const newWidth = Math.min(maxAllowedWidth, historyDims.width);
    const newHeight = newWidth * aspectRatio;

    const x = printableArea.left + (printableArea.width - newWidth) / 2;
    const y = printableArea.top;

    return { x, y, width: newWidth, height: newHeight };
  }, [currentOperation, printableArea]);

  // Helper function to update Konva image with new image and dimensions
  const updateKonvaImage = useCallback(
    (
      img: HTMLImageElement,
      dimensions: { x: number; y: number; width: number; height: number }
    ) => {
      if (!imageRef.current?.getLayer()) return;

      // Update the Konva image with the new image data
      imageRef.current.image(img);
      imageRef.current.setAttrs({
        x: dimensions.x,
        y: dimensions.y,
        width: dimensions.width,
        height: dimensions.height,
        cropX: 0,
        cropY: 0,
        cropWidth: img.width,
        cropHeight: img.height,
        scaleX: 1,
        scaleY: 1
      });

      // Redraw the layer
      imageRef.current.getLayer()?.batchDraw();
    },
    []
  );

  // Watch for currentImageData changes and restore the image
  useEffect(() => {
    if (!currentOperation) return;
    if (!currentImageData || !imageRef.current) return;

    const img = new window.Image();
    img.onload = () => {
      if (!imageRef.current?.getLayer()) return;
      if (displayDimensions) {
        updateKonvaImage(img, displayDimensions);
      } else {
        imageRef.current.image(img);
        imageRef.current.getLayer()?.batchDraw();
      }
    };
    img.src = currentImageData;
  }, [currentImageData, currentOperation, displayDimensions, updateKonvaImage]);

  if (!currentOperation || !currentOperation.metadata) return null;

  return (
    <Group ref={groupRef}>
      {currentOperation && displayDimensions && (
        <Image
          ref={imageRef}
          image={artworkImage}
          x={displayDimensions.x}
          y={displayDimensions.y}
          width={displayDimensions.width}
          height={displayDimensions.height}
          draggable={false}
          listening={true}
        />
      )}

      {/* Show simple border in cursor and pan modes */}
      {(interactionMode === "cursor" || interactionMode === "pan") &&
        currentOperation &&
        displayDimensions && (
          <Rect
            x={displayDimensions.x}
            y={displayDimensions.y}
            width={displayDimensions.width}
            height={displayDimensions.height}
            stroke="#4864F9"
            strokeWidth={0.5}
            dash={[4, 2]}
            listening={false}
          />
        )}

      {/* Only render CropTransformer in crop mode */}
      {interactionMode === "crop" && currentOperation && printableArea && (
        <CropTransformer
          node={imageRef.current}
          printableArea={printableArea}
          onTransformingChange={t => {
            isCroppingRef.current = t;
          }}
          onCropCommit={({ imageData, newDimensions, cropInfo }) => {
            const newImg = new window.Image();
            newImg.onload = () => {
              // Calculate crop area (pixels cut from each side) using the actual crop info
              const cropArea = {
                top: Math.round(cropInfo.cropY),
                bottom: Math.round(
                  cropInfo.originalHeight - (cropInfo.cropY + cropInfo.cropHeight)
                ),
                left: Math.round(cropInfo.cropX),
                right: Math.round(cropInfo.originalWidth - (cropInfo.cropX + cropInfo.cropWidth))
              };

              const sizeBefore = {
                width: Math.round(cropInfo.originalWidth),
                height: Math.round(cropInfo.originalHeight)
              };

              const sizeAfter = {
                width: Math.round(cropInfo.cropWidth),
                height: Math.round(cropInfo.cropHeight)
              };

              // Track the crop event
              trackImageEditCrop({
                artworkId,
                cropArea,
                sizeBefore,
                sizeAfter
              });

              pushOperation({
                type: "crop",
                imageData: imageData.toDataURL(),
                metadata: {
                  dimensions: newDimensions
                },
                timestamp: Date.now()
              });
            };
            newImg.src = imageData.toDataURL();
          }}
        />
      )}

      {currentOperation && displayDimensions && (
        <EraseOverlay
          imageRef={imageRef}
          displayDimensions={displayDimensions}
          activeTool={activeTool}
          artworkId={artworkId}
          interactionMode={interactionMode}
          isSpaceHeld={isSpaceHeld}
        />
      )}
    </Group>
  );
}
